import type {Translator} from '@yandex-delivery/core/i18n'
import type {PropsWithChildren} from 'react'
import React, {lazy, Suspense} from 'react'

import {logEvent} from '../event'
import {getRumGlobal} from '../utils'

type ErrorBoundaryProps = PropsWithChildren<{
  i18n: Translator
  reqId?: string
}>

type ErrorBoundaryState = {
  hasError: boolean
}

const ErrorNotify = lazy(
  () => import(/* webpackChunkName: 'error-boundary-notify' */ '@yandex-delivery/blocks/error/ErrorNotify'),
)

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private fallbackComponent: JSX.Element

  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {hasError: false}
    
    const t = props.i18n
    const rum = getRumGlobal()
    
    // Пытаемся получить reqId из разных источников
    let reqId: string | undefined = props.reqId
    
    if (!reqId && rum) {
      // @ts-expect-error
      reqId = rum.getErrorSetting?.('reqid')
      // @ts-expect-error
      if (!reqId && rum._vars) {
        // @ts-expect-error
        reqId = rum._vars['reqid'] || rum._vars['x-yarequestid']
      }
    }
    
    this.fallbackComponent = <Suspense children={<ErrorNotify {...{reqId, t}} />} />
  }

  static getDerivedStateFromError() {
    return {hasError: true}
  }

  // @ts-expect-error Ya.Rum типы загружаются глобально
  componentDidCatch(error: Ya.Rum.LoggableError, additional: React.ErrorInfo) {
    // @ts-expect-error
    const {logError} = getRumGlobal() || {}
    // @ts-expect-error
    const commonArgs: [Ya.Rum.LogErrorOptions, Ya.Rum.LoggableError] = [
      {additional, type: 'error-boundary'},
      error || new Error('MISSING_ERROR'),
    ]

    logError?.(...commonArgs)
    logEvent('error_boundary_shown')
  }

  render() {
    return this.state.hasError ? this.fallbackComponent : this.props.children
  }
}
